import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Spinner,
  Alert,
  Card,
  Badge,
  Col,
  Row,
} from "react-bootstrap";
import SidebarBoarding from "../../components/SidebarDashboard";
import CustomNavbar from "../../components/CustomNavbar";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import esLocale from "@fullcalendar/core/locales/es";
import moment from "moment";
import { getTrainingsEmployeesCalendar } from "../../redux/capacitacionDesarrollo/campaigns/actions";

function TrainingCalendar() {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState({
    start: moment().startOf("week"),
    end: moment().endOf("week"),
  });
  const prevDateRange = useRef(currentDateRange);

  //Store
  const userCode = useSelector((store) => store.auth.username);

  const {
    loaderTrainingsEmployeesCalendar,
    trainingsEmployeesCalendar,
    trainingsEmployeesCalendarFail,
  } = useSelector((state) => state.campaigns);

  const handleDatesSet = (arg) => {
    const newDateRange = {
      start: moment(arg.start),
      end: moment(arg.end),
    };

    if (
      !newDateRange.start.isSame(prevDateRange.current.start, "day") ||
      !newDateRange.end.isSame(prevDateRange.current.end, "day")
    ) {
      setCurrentDateRange(newDateRange);
      prevDateRange.current = newDateRange;
    }
  };

  useEffect(() => {
    if (userCode) {
      setSelectedEvent(null);
      dispatch(
        getTrainingsEmployeesCalendar({
          codigo_empleado: userCode,
          fecha_inicio: currentDateRange.start.format("YYYY-MM-DD"),
          fecha_fin: currentDateRange.end.format("YYYY-MM-DD"),
        })
      );
    }
  }, [dispatch, userCode, currentDateRange]);

  const onShowSidebar = () => setShow(!show);

  const transformTrainingsToEvents = () => {
    if (!trainingsEmployeesCalendar?.results) return [];

    return trainingsEmployeesCalendar.results
      .map((training) => ({
        id: training.id,
        title: training.nombre_evento_capacitacion,
        start: training.fecha_inicio
          ? `${training.fecha_inicio}T${
              training.hora_capacitacion || "00:00:00"
            }`
          : null,
        extendedProps: {
          ...training,
        },
        backgroundColor: training.estado?.color || "#3788d8",
        borderColor: training.estado?.color || "#3788d8",
        textColor: "#ffffff",
      }))
      .filter((event) => event.start);
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event.extendedProps);
  };

  const handleCloseDetails = () => {
    setSelectedEvent(null);
  };

  return (
    <>
      <SidebarBoarding show={show} onShowSidebar={onShowSidebar} />
      <CustomNavbar type={"dashboard"} onShowSidebar={onShowSidebar} />
      <div className="custom-wrapper">
        <div>
          <Container fluid>
            <div style={{ padding: "1rem" }}>
              <h6 className="font-weight-bold text-uppercase">
                Calendario de Capacitaciones
              </h6>
            </div>

            {loaderTrainingsEmployeesCalendar && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Spinner animation="border" role="status" />
                  <span style={{ marginLeft: "10px" }}>
                    Cargando capacitaciones...
                  </span>
                </div>
              </div>
            )}

            {trainingsEmployeesCalendarFail && (
              <Alert variant="danger" className="mt-3">
                Error al cargar las capacitaciones
              </Alert>
            )}

            <div
              style={{
                marginTop: "1rem",
                height: "calc(100vh - 180px)",
                overflow: "hidden",
              }}
            >
              <Row style={{ height: "100%" }}>
                <Col
                  md={selectedEvent ? 8 : 12}
                  style={{ height: "100%", overflowY: "auto" }}
                >
                  <div style={{ height: "100%" }}>
                    <FullCalendar
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                        multiMonthPlugin,
                      ]}
                      initialView="timeGridWeek"
                      initialDate={moment().toDate()}
                      locales={[esLocale]}
                      locale="es"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right:
                          "dayGridMonth,timeGridWeek,timeGridDay,multiMonthYear",
                      }}
                      views={{
                        multiMonthYear: {
                          type: "multiMonth",
                          duration: { months: 12 },
                          buttonText: "Año",
                        },
                        dayGridMonth: {
                          buttonText: "Mes",
                        },
                        timeGridWeek: {
                          buttonText: "Semana",
                        },
                        timeGridDay: {
                          buttonText: "Día",
                        },
                      }}
                      height="auto"
                      contentHeight="auto"
                      editable={false}
                      selectable={false}
                      events={transformTrainingsToEvents()}
                      eventClick={handleEventClick}
                      datesSet={handleDatesSet}
                      eventContent={(eventInfo) => (
                        <div style={{ padding: "2px" }}>
                          <b>{moment(eventInfo.event.start).format("HH:mm")}</b>
                          <div>{eventInfo.event.title}</div>
                        </div>
                      )}
                    />
                  </div>
                </Col>

                {selectedEvent && (
                  <Col md={4} style={{ height: "100%", overflowY: "auto" }}>
                    <Card className="shadow-sm" style={{ height: "100%" }}>
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Detalles de la Capacitación</h5>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleCloseDetails}
                        >
                          &times;
                        </button>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-3">
                          <h4>{selectedEvent.nombre_evento_capacitacion}</h4>
                          <Badge
                            pill
                            style={{
                              backgroundColor: selectedEvent.estado?.color,
                              color: "white",
                            }}
                          >
                            {selectedEvent.estado?.descripcion}
                          </Badge>
                        </div>

                        <Row className="mb-2">
                          <Col xs={12}>
                            <strong>Fecha inicio:</strong>
                            <p>
                              {moment(selectedEvent.fecha_inicio).format(
                                "DD/MM/YYYY"
                              )}
                            </p>
                          </Col>
                          <Col xs={6}>
                            <strong>Fecha fin:</strong>
                            <p>
                              {moment(selectedEvent.fecha_fin).format(
                                "DD/MM/YYYY"
                              )}
                            </p>
                          </Col>
                        </Row>

                        <div className="mb-2">
                          <strong>Hora:</strong>
                          <p>
                            {selectedEvent.hora_capacitacion ||
                              "No especificada"}
                          </p>
                        </div>

                        <div className="mb-2">
                          <strong>Lugar:</strong>
                          <p>{selectedEvent.lugar || "No especificado"}</p>
                        </div>

                        {selectedEvent.responsable && (
                          <div className="mb-2">
                            <strong>Responsable:</strong>
                            <p>
                              {selectedEvent.responsable.nombre} (
                              {selectedEvent.responsable.codigo})
                            </p>
                          </div>
                        )}

                        <div className="mb-2">
                          <strong>Objetivo:</strong>
                          <p>
                            {selectedEvent.objetivo_capacitacion ||
                              "No especificado"}
                          </p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>
            </div>
          </Container>
        </div>
      </div>
    </>
  );
}

export default TrainingCalendar;
